import { supabase } from '@/integrations/supabase/client';
import { offlineDB, OfflineRun } from '@/lib/offline-db';
import { logger } from '@/lib/logger';

class SyncService {
  private syncInProgress = false;
  private listeners: Array<(status: SyncStatus) => void> = [];

  constructor() {
    this.startAutoSync();
  }

  private startAutoSync() {
    setInterval(() => {
      this.syncPendingRuns();
    }, 30000);
  }

  async syncPendingRuns(userId?: string): Promise<SyncResult> {
    if (this.syncInProgress) {
      logger.info('Sync already in progress, skipping');
      return { success: 0, failed: 0, pending: 0 };
    }

    // Verificar sessão antes de tentar sincronizar
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      logger.debug('Skipping sync: No active session');
      return { success: 0, failed: 0, pending: 0 };
    }

    this.syncInProgress = true;
    this.notifyListeners({ status: 'syncing', progress: 0 });

    try {

      const targetUserId = userId || session.user.id;
      const pendingRuns = await offlineDB.getPendingRuns(targetUserId);

      if (pendingRuns.length === 0) {
        logger.info('No pending runs to sync');
        this.notifyListeners({ status: 'idle', progress: 100 });
        return { success: 0, failed: 0, pending: 0 };
      }

      logger.info(`Syncing ${pendingRuns.length} offline runs`);

      let successCount = 0;
      let failedCount = 0;

      for (let i = 0; i < pendingRuns.length; i++) {
        const run = pendingRuns[i];
        const progress = ((i + 1) / pendingRuns.length) * 100;

        this.notifyListeners({ 
          status: 'syncing', 
          progress,
          currentRun: i + 1,
          totalRuns: pendingRuns.length
        });

        try {
          await offlineDB.updateRunSyncStatus(run.id, 'syncing');

          const result = await this.processOfflineRun(run);

          if (result.success) {
            await offlineDB.updateRunSyncStatus(run.id, 'synced');
            successCount++;
          } else {
            await offlineDB.updateRunSyncStatus(
              run.id, 
              'failed', 
              result.error
            );
            failedCount++;
          }

          await new Promise(resolve => setTimeout(resolve, 1000));

        } catch (error) {
          logger.error('Failed to sync run', error, { runId: run.id });
          await offlineDB.updateRunSyncStatus(
            run.id,
            'failed',
            error instanceof Error ? error.message : 'Unknown error'
          );
          failedCount++;
        }
      }

      const remainingPending = pendingRuns.length - successCount - failedCount;

      this.notifyListeners({ 
        status: failedCount > 0 ? 'error' : 'success', 
        progress: 100,
        successCount,
        failedCount
      });

      logger.info('Sync completed', { 
        success: successCount, 
        failed: failedCount,
        pending: remainingPending
      });

      return {
        success: successCount,
        failed: failedCount,
        pending: remainingPending
      };

    } catch (error) {
      logger.error('Sync process failed', error);
      this.notifyListeners({ status: 'error', progress: 0 });
      throw error;
    } finally {
      this.syncInProgress = false;
    }
  }

  private async processOfflineRun(run: OfflineRun): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.functions.invoke('process-run', {
        body: {
          user_id: run.userId,
          gps_points: run.gpsPoints,
          started_at: new Date(run.startedAt).toISOString(),
          finished_at: new Date(run.finishedAt).toISOString(),
          duration: run.duration,
          distance: run.distance,
          bpm_readings: run.bpmReadings || [],
          posture_data: run.postureData,
          music_stats: run.musicStats
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.run_id) {
        throw new Error('No run_id returned from server');
      }

      if (run.caption || run.emotion) {
        await supabase
          .from('runs')
          .update({ 
            emotion: run.emotion,
            caption: run.caption 
          })
          .eq('id', data.run_id);
      }

      logger.info('Offline run synced successfully', { 
        localRunId: run.id,
        serverRunId: data.run_id 
      });

      return { success: true };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Failed to process offline run', error, { runId: run.id });
      return { success: false, error: errorMessage };
    }
  }

  onSyncStatusChange(callback: (status: SyncStatus) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  private notifyListeners(status: SyncStatus) {
    this.listeners.forEach(listener => listener(status));
  }

  // ✅ APENAS PARA TESTES: Resetar estado de sync
  resetForTesting() {
    if (process.env.NODE_ENV === 'test' || process.env.VITEST === 'true') {
      this.syncInProgress = false;
      this.listeners = [];
    }
  }
}

interface SyncStatus {
  status: 'idle' | 'syncing' | 'success' | 'error';
  progress: number;
  currentRun?: number;
  totalRuns?: number;
  successCount?: number;
  failedCount?: number;
}

interface SyncResult {
  success: number;
  failed: number;
  pending: number;
}

export const syncService = new SyncService();
export type { SyncStatus, SyncResult };
